import React, { useEffect, useMemo } from 'react'

import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticProps } from 'next'
import { ArticleJsonLd, NextSeo } from 'next-seo'
import Head from 'next/head'

import { getLayout } from '@/components/layouts/opportunity/Layout'
import { useBreadcrumb } from '@/components/opportunity/common/BreadcrumbV2/context'
import {
  OpportunityDetailShell,
  OpportunityPageLayout,
} from '@/components/opportunity/common/OpportunityDetailShell'
import { normalizeDepartmentName } from '@/components/opportunity/common/transforms'
import { SbirDisplayV2 } from '@/components/opportunity/Sbir/DisplayV2'
import { SbirDisplayV2Skeleton } from '@/components/opportunity/Sbir/DisplayV2/Skeleton'

import { FRONTEND_URL } from '@/constants/env'

import { capitalizeString } from '@/helpers/string'

import { useShare } from '@/hooks/common/useShare'
import { useIndexOpportunity } from '@/hooks/opportunity/useIndexOpportunity'
import {
  fetchSbirWithoutPresignedUrls,
  sbirWithoutPresignedUrlsQueryKey,
  useSbirPresignedUrls,
  useSuspenseSbirWithoutPresignedUrls,
} from '@/hooks/sbir/useSbir'
import { useSimilarOpportunities } from '@/hooks/similar-opportunities'

import type { NextPageWithLayout, WithRenderedAt } from '@/types/next'

type SbirDetailPageProps = WithRenderedAt & {
  id: string
  seoTitle: string
  dehydratedState: ReturnType<typeof dehydrate>
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps = (async (context) => {
  const id = context.params?.id
  const notFoundResponse = {
    redirect: {
      destination: '/404',
      permanent: false,
    },
  }

  if (id === undefined || typeof id !== 'string') return notFoundResponse

  try {
    const sbir = await fetchSbirWithoutPresignedUrls(id)

    if (!sbir) return notFoundResponse

    const queryClient = new QueryClient()
    queryClient.setQueryData(sbirWithoutPresignedUrlsQueryKey(id), sbir)

    return {
      props: {
        id,
        seoTitle: sbir.title ?? 'SBIR/STTR Opportunity',
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        __renderedAt: new Date().toISOString(),
      },
      revalidate: 7200, // revalidate every 2 hours
    }
  } catch (e) {
    if (e instanceof Response && e.status === 404) {
      return notFoundResponse
    }
    if (!(e instanceof Error)) {
      const errorMessage = JSON.stringify(e)
      throw new Error(`error occurred while fetching SBIR: ${errorMessage}`)
    }
    throw e
  }
}) satisfies GetStaticProps<SbirDetailPageProps>

/**
 * SBIR/STTR Detail Page (Public, ISR)
 *
 * Displays SBIR/STTR opportunity details using the V2 design.
 * Data is pre-fetched via getStaticProps, dehydrated into the React Query cache,
 * and revalidated every 2 hours. Similar opportunities load progressively client-side.
 *
 * Path: /sbirs/[id]
 */
const SbirDetailPage: NextPageWithLayout = ({
  id,
  seoTitle,
  dehydratedState,
}: SbirDetailPageProps) => {
  return (
    <OpportunityDetailShell
      seoTitle={seoTitle}
      dehydratedState={dehydratedState}
      errorTitle="Failed to load SBIR opportunity"
      notFoundTitle="Opportunity Not Found"
      backHref="/sbirs/search"
      backLabel="Browse SBIR/STTR"
      fallback={
        <OpportunityPageLayout>
          <SbirDisplayV2Skeleton />
        </OpportunityPageLayout>
      }
    >
      <SbirDetailContent id={id} />
    </OpportunityDetailShell>
  )
}

function SbirDetailContent({ id }: { id: string }) {
  useIndexOpportunity(id, 'sbir')

  // The ISR copy can be hours old, so the live fetch replaces it once it loads.
  const { data: baseSbir } = useSuspenseSbirWithoutPresignedUrls(id)
  const { data: presignedSbir } = useSbirPresignedUrls(id)
  const sbir = presignedSbir ?? baseSbir

  // Fetch similar opportunities (loads client-side)
  const { data: similarOpportunities, isLoading: isSimilarLoading } =
    useSimilarOpportunities('sbir', id)

  const { setItems, clearItems } = useBreadcrumb()

  useEffect(() => {
    if (sbir) {
      const agencyDisplay = normalizeDepartmentName(sbir.agency) ?? sbir.agency
      setItems([
        { name: 'SBIR/STTR', href: '/sbirs/search' },
        {
          name: agencyDisplay,
          href: `/sbirs/search?agencies=${encodeURIComponent(sbir.agency)}`,
        },
        { name: sbir.number ?? sbir.title },
      ])
    }
    return () => clearItems()
  }, [sbir, setItems, clearItems])

  const handleShare = useShare()

  const title = sbir?.title ?? 'SBIR/STTR Opportunity'

  const seoDescription = useMemo(() => {
    if (!sbir) return ''
    return sbir.ai_summary.split('. ').slice(0, 3).join('. ') + '.'
  }, [sbir])

  const ogImageUrl = useMemo(() => {
    if (!sbir) return ''
    let url = `${FRONTEND_URL}/api/og?title=${encodeURIComponent(sbir.title)}`
    if (sbir.agency) {
      url += `&description=${encodeURIComponent(capitalizeString(sbir.agency))}`
    }
    url += `&subdescription=${encodeURIComponent('SBIR/STTR')}`
    return url
  }, [sbir])

  return (
    <>
      <NextSeo
        title={title}
        description={seoDescription}
        additionalLinkTags={[
          {
            rel: 'icon',
            href: `${FRONTEND_URL}/favicon.ico`,
          },
        ]}
        openGraph={{
          description: seoDescription,
          images: [
            {
              url: ogImageUrl,
              width: 1200,
              height: 632,
              alt: title,
            },
          ],
          siteName: 'Sweetspot',
          title: title,
          url: `${FRONTEND_URL}/sbirs/${sbir.id}`,
        }}
        twitter={{
          handle: '@SweetspotGov',
          site: '@SweetspotGov',
          cardType: 'summary_large_image',
        }}
      />
      <ArticleJsonLd
        url={`${FRONTEND_URL}/sbirs/${sbir.id}`}
        title={title}
        images={[ogImageUrl]}
        datePublished={sbir.release_date}
        dateModified={sbir.release_date}
        authorName={[
          {
            name: 'Sweetspot',
            url: FRONTEND_URL,
          },
        ]}
        publisherName="Sweetspot"
        publisherLogo={`${FRONTEND_URL}/logo.png`}
        description={sbir.ai_summary}
        isAccessibleForFree={true}
      />
      {/*
        for some reason, Twitter cards using standard OG tags don't work, need to add this manually;
        https://github.com/garmeeh/next-seo/issues/14;
      */}
      <Head>
        <meta name="robots" content="noarchive" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:image" content={ogImageUrl} />
        <meta property="application-name" content="Sweetspot" />
      </Head>
      <OpportunityPageLayout>
        <SbirDisplayV2
          sbir={sbir}
          similarOpportunities={similarOpportunities}
          isSimilarLoading={isSimilarLoading}
          onShare={handleShare}
        />
      </OpportunityPageLayout>
    </>
  )
}

SbirDetailPage.getLayout = getLayout
SbirDetailPage.requiresAuth = false

export default SbirDetailPage
